import {ApolloClient, gql} from "@apollo/client";
import {
  GetJobImportStatusResponse,
  Job,
  JobImportOrderInput,
  JobImportStopInput,
  SearchCustomersDocument,
  SearchCustomersQuery,
  SearchCustomersQueryVariables,
  SearchServicesDocument,
  SearchServicesQuery,
  SearchServicesQueryVariables,
  SearchVehicleTypesDocument,
  SearchVehicleTypesQuery,
  SearchVehicleTypesQueryVariables,
  SubmitJobImportDocument,
  SubmitJobImportMutation,
  SubmitJobImportMutationVariables
} from "../../../generated/graphql";
import {buildFieldTree, treeToGraphQL} from "../../../utils/dynamic-query/dynamic-query-builder";
import {IP_JOB_DEFAULT_FIELDS} from "../ipJobFields";
import {CsvImportKnownValues, CsvImportOrder, CsvImportStatus, CsvImportStop} from "./types";

// The backend calls of an import (DP-3261): submitJobImport starts the asynchronous order creation,
// getJobImportStatus is polled every 2 s until every created order's job is ready for planning.

// What the api needs from Apollo; the dialog passes its client in
export type CsvImportClient = Pick<ApolloClient<object>, "mutate" | "query">;

// The backend caps a createdOrders page at 500, which is also the file's order limit
export const CSV_IMPORT_STATUS_PAGE_SIZE = 500;

// Only the fields of the input type: rowNumber is for the preview table and AppSync rejects unknown fields
const toStopInput = (stop: CsvImportStop): JobImportStopInput => ({
  stopType: stop.stopType,
  name: stop.name,
  address: stop.address,
  city: stop.city,
  state: stop.state,
  zip: stop.zip,
  phone: stop.phone,
  room: stop.room,
  note: stop.note,
  scheduledDateTime: stop.scheduledDateTime,
  earlyDateTime: stop.earlyDateTime,
  lateDateTime: stop.lateDateTime,
  pieces: stop.pieces,
  weight: stop.weight
});

export const toJobImportOrderInput = (order: CsvImportOrder): JobImportOrderInput => ({
  orderNumber: order.orderNumber,
  customerCode: order.customerCode,
  service: order.service,
  orderReference: order.orderReference,
  vehicle: order.vehicle,
  caller: order.caller,
  stops: order.stops.map(toStopInput)
});

// Well above any tenant's count of services or vehicle types
const KNOWN_VALUES_LIMIT = 1000;

/** The tenant's service codes and vehicle type descriptions, which the file's Service and Vehicle are checked against. */
export const getCsvImportKnownValues = async (client: CsvImportClient): Promise<CsvImportKnownValues> => {
  const [services, vehicleTypes] = await Promise.all([
    client.query<SearchServicesQuery, SearchServicesQueryVariables>({
      query: SearchServicesDocument,
      variables: {limit: KNOWN_VALUES_LIMIT}
    }),
    client.query<SearchVehicleTypesQuery, SearchVehicleTypesQueryVariables>({
      query: SearchVehicleTypesDocument,
      variables: {limit: KNOWN_VALUES_LIMIT}
    })
  ]);
  return {
    services: (services.data.searchServices?.items ?? []).flatMap((item) => (item.service ? [item.service] : [])),
    vehicles: (vehicleTypes.data.searchVehicleTypes?.items ?? []).flatMap((item) =>
      item?.description ? [item.description] : []
    )
  };
};

/**
 * Returns the tenant's customer codes among `codes`. Only the file's codes are looked up, since a tenant can have
 * far more customers than services. A wildcard without * or ? is an exact match in any case, as CMS matches them.
 */
export const findCustomerCodes = async (client: CsvImportClient, codes: string[]): Promise<string[]> => {
  const {data} = await client.query<SearchCustomersQuery, SearchCustomersQueryVariables>({
    query: SearchCustomersDocument,
    variables: {
      filter: {or: [...new Set(codes)].map((code) => ({customerCode: {wildcard: code.replace(/[\\*?]/g, "\\$&")}}))},
      limit: KNOWN_VALUES_LIMIT
    },
    // A customer added after the last pick must count on the next one
    fetchPolicy: "no-cache"
  });
  return (data.searchCustomers?.items ?? []).flatMap((item) => (item.customerCode ? [item.customerCode] : []));
};

/** Starts creating the orders and returns the import id to poll. */
export const submitCsvImportOrders = async (client: CsvImportClient, orders: CsvImportOrder[]): Promise<string> => {
  const {data} = await client.mutate<SubmitJobImportMutation, SubmitJobImportMutationVariables>({
    mutation: SubmitJobImportDocument,
    variables: {input: {orders: orders.map(toJobImportOrderInput)}}
  });
  const importId = data?.submitJobImport.importId;
  if (!importId) throw new Error("The import was not started");
  return importId;
};

// The job selection is the IP Jobs grid's field list plus the grid columns the grid fetches through its column
// definitions (Customer Account, Vehicle), so the imported jobs behave and display like grid-selected ones
export const CSV_IMPORT_JOB_FIELDS = [...IP_JOB_DEFAULT_FIELDS, "order.customer.name", "vehicleType.description"];

export const GET_JOB_IMPORT_STATUS = gql(/* GraphQL */ `
  query GetJobImportStatus($input: GetJobImportStatusInput!) {
    getJobImportStatus(input: $input) {
      importId
      status
      total
      created
      failed
      ready
      error
      createdOrdersTotal
      failedOrders {
        orderNumber
        error
      }
      createdOrders {
        orderNumber
        createdOrderNumber
        job {
${treeToGraphQL(buildFieldTree(CSV_IMPORT_JOB_FIELDS), "          ")}
        }
      }
    }
  }
`);

type StatusPage = {getJobImportStatus: GetJobImportStatusResponse};

/**
 * The import's progress: counts and failed orders while it runs; once every created order's job is in the
 * jobs index (`ready`), the created orders with their jobs, fetched page by page.
 */
export const getCsvImportStatus = async (
  client: CsvImportClient,
  importId: string,
  signal?: AbortSignal
): Promise<CsvImportStatus> => {
  const fetchPage = async (offset: number) => {
    const {data} = await client.query<StatusPage>({
      query: GET_JOB_IMPORT_STATUS,
      variables: {input: {importId, createdOrdersOffset: offset, createdOrdersLimit: CSV_IMPORT_STATUS_PAGE_SIZE}},
      fetchPolicy: "no-cache",
      context: {fetchOptions: {signal}}
    });
    return data.getJobImportStatus;
  };

  type CreatedPage = NonNullable<GetJobImportStatusResponse["createdOrders"]>;
  const first = await fetchPage(0);
  let createdOrders: CreatedPage | undefined = first.ready ? first.createdOrders ?? [] : undefined;
  if (createdOrders) {
    const total = first.createdOrdersTotal ?? createdOrders.length;
    while (createdOrders.length < total && !signal?.aborted) {
      const page: CreatedPage | null | undefined = (await fetchPage(createdOrders.length)).createdOrders;
      // A later page without orders (the backend's lookup for it failed or timed out) means the import
      // is not ready after all: report it that way so the next poll tries again, instead of a short list
      if (!page?.length) {
        createdOrders = undefined;
        break;
      }
      createdOrders = [...createdOrders, ...page];
    }
  }

  return {
    status: first.status,
    error: first.error ?? undefined,
    total: first.total,
    created: first.created,
    failed: first.failed,
    failedOrders: first.failedOrders.map(({orderNumber, error}) => ({orderNumber, error})),
    createdOrders: createdOrders?.map(({orderNumber, createdOrderNumber, job}) => ({
      orderNumber,
      createdOrderNumber,
      job: job as Job
    }))
  };
};
